// Platform health — Verso-wide operational visibility: agent fleet freshness,
// per-org footprint, platform totals, and a 24h activity pulse. Polls the
// aggregate endpoint every 30s.

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';

function StatCard({ label, value, sub, tone = 'default' }) {
  const toneCls = {
    default: 'text-gray-900',
    good:    'text-green-700',
    bad:     'text-red-600',
  }[tone];
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${toneCls}`}>{value}</div>
      {sub && <div className="text-xs text-gray-400">{sub}</div>}
    </div>
  );
}

function fmtWhen(ts) {
  if (!ts) return 'never';
  const d = new Date(String(ts).includes('T') ? ts : `${String(ts).replace(' ', 'T')}Z`);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 24 * 60) return `${Math.round(mins / 60)}h ago`;
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function PlatformHealth() {
  const { data, isLoading, isError, dataUpdatedAt } = useQuery({
    queryKey: ['platform-health'],
    queryFn: () => api.get('/verso-admin/platform-health'),
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        Couldn&rsquo;t load platform health.
      </div>
    );
  }

  const { agents = [], agents_online, agents_total, orgs = [], totals = {}, users = {}, activity = {} } = data;
  const staleAgents = agents.filter((a) => a.stale);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Platform Health</h1>
        <span className="text-xs text-gray-400">refreshed {fmtWhen(new Date(dataUpdatedAt).toISOString())} · auto every 30s</span>
      </div>

      {/* Fleet + footprint summary */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
        <StatCard
          label="PBX agents"
          value={`${agents_online}/${agents_total}`}
          sub={staleAgents.length > 0 ? `${staleAgents.length} stale` : 'all reporting'}
          tone={staleAgents.length > 0 ? 'bad' : 'good'}
        />
        <StatCard label="Organizations" value={totals.organizations ?? '—'} />
        <StatCard label="Subscribers" value={totals.subscribers ?? '—'} />
        <StatCard label="DIDs" value={totals.dids ?? '—'} />
        <StatCard label="Devices" value={totals.devices ?? '—'} sub={`${totals.sip_accounts ?? 0} extensions`} />
        <StatCard label="Users" value={users.active ?? '—'}
                  sub={`${users.platform_admins ?? 0} platform admins · ${users.deactivated ?? 0} deactivated`} />
      </div>

      {/* Activity pulse (24h) */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Audit events · 24h" value={activity.audit_24h ?? 0}
                  sub={`last ${fmtWhen(activity.last_audit_at)}`} />
        <StatCard label="Logins · 24h" value={activity.logins_24h ?? 0} />
        <StatCard label="Voicemails ingested · 24h" value={activity.voicemails_24h ?? 0}
                  sub={`last ${fmtWhen(activity.last_voicemail_ingest)}`} />
        <StatCard label="Voicemails stored" value={totals.voicemails ?? 0} />
      </div>

      {/* Agent fleet */}
      <div>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray-500">Agent fleet</h2>
        <div className="rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Host</th>
                <th className="px-4 py-2">Organization</th>
                <th className="px-4 py-2">Version</th>
                <th className="px-4 py-2">Last heartbeat</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {agents.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-400">No agents registered.</td></tr>
              )}
              {agents.map((a) => (
                <tr key={a.agent_id} className={a.stale ? 'bg-red-50/50' : ''}>
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
                      a.stale ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${a.stale ? 'bg-red-500' : 'bg-green-500'}`} />
                      {a.stale ? 'stale' : 'online'}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-medium" title={a.agent_id}>{a.name || a.agent_id}</td>
                  <td className="px-4 py-2 text-gray-600">
                    {a.org_id
                      ? <Link to={`/orgs/${a.org_id}`} className="text-blue-600 hover:underline">{a.org_name || a.org_id}</Link>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">{a.version || '—'}</td>
                  <td className="px-4 py-2 text-gray-600">{fmtWhen(a.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-org footprint */}
      <div>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray-500">Organizations</h2>
        <div className="rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-4 py-2">Organization</th>
                <th className="px-4 py-2">Slug</th>
                <th className="px-4 py-2">Subscribers</th>
                <th className="px-4 py-2">Agents</th>
                <th className="px-4 py-2">Users</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orgs.map((o) => (
                <tr key={o.org_id}>
                  <td className="px-4 py-2 font-medium">
                    <Link to={`/orgs/${o.org_id}`} className="text-blue-600 hover:underline">{o.name}</Link>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">{o.slug}</td>
                  <td className="px-4 py-2 text-gray-600">{o.subscribers}</td>
                  <td className="px-4 py-2 text-gray-600">{o.agents}</td>
                  <td className="px-4 py-2 text-gray-600">{o.users}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
